"use client";

import './scrollable-container.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useEffect } from 'react';
import styles from './scrollable-container.module.scss.js';
import checkIfMobileDevice from '../../utilities/check-device/index.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import '../../_virtual/_tslib.js';
import '../../utilities/use-viewport/index.js';
import { _onHorizontalTrackMouseDown, _onVerticalTrackMouseDown, getThumbHorizontalWidth, getScrollLeftForOffset, getThumbVerticalHeight, getScrollTopForOffset, getInnerWidth, getInnerHeight } from './utils.js';

/**
 * @component ScrollableContainer
 * @package Scrollable
 */
const ScrollableContainer = ({ scrollableId, contentStyle, height, width, children, surface = 'lightPrimary', hideVerticalScroll, hideHorizontalScroll, onScroll, onUpdate, onScrollStart, onScrollStop, hideTracksWhenNotNeeded, 'data-testid': testId, hitAreaWidth = 44, minimizeHitArea, overflowOrientation = 'vertical', contentMargin, horizontalScrollbarInset = {
    top: '0px',
    right: '4px',
    bottom: '4px',
    left: '4px',
}, verticalScrollbarInset = {
    top: '4px',
    right: '4px',
    bottom: '4px',
    left: '0px',
}, scrollContainerInset, tabIndex, }) => {
    const content = useRef(null);
    const trackHorizontal = useRef(null);
    const thumbHorizontal = useRef(null);
    const trackVertical = useRef(null);
    const thumbVertical = useRef(null);
    const scrollView = useRef(null);
    const childContentResizeObserver = useRef(null);
    const prevHeight = useRef(0);
    const verticalTrackStyles = [styles.trackVertical];
    const verticalThumbStyles = [styles.thumbVertical];
    const containerStyles = [
        styles.container,
        styles[surface.toLocaleLowerCase()],
        styles[overflowOrientation],
    ];
    const verticalTrackGrabzoneStyles = [styles.verticalTrackGrabZone];
    const verticalThumbGrabzoneStyles = [styles.thumbVerticalGrabZone];
    const horizontalTrackGrabzoneStyles = [styles.horizontalTrackGrabZone];
    const horizontalTrackStyles = [styles.trackHorizontal];
    const horizontalThumbGrabzoneStyles = [styles.thumbHorizontalGrabZone];
    const horizontalThumbStyles = [styles.thumbHorizontal];
    /* istanbul ignore else -- @preserve */
    if (hideVerticalScroll) {
        verticalTrackGrabzoneStyles.push(styles.hideScroll);
        verticalThumbStyles.push(styles.hideScroll);
    }
    /* istanbul ignore else -- @preserve */
    if (minimizeHitArea)
        verticalTrackGrabzoneStyles.push(styles.minimizeHitArea);
    if (hideHorizontalScroll)
        horizontalTrackGrabzoneStyles.push(styles.hideScroll);
    const [lastViewScroll, setLastViewScroll] = useState({ left: 0, top: 0 });
    const requestFrame = useRef(undefined);
    const scrolling = useRef(false);
    const dragging = useRef(false);
    const hideTracksTimeout = useRef(undefined);
    const detectScrollingInterval = useRef(undefined);
    const trackMouseOver = useRef(false);
    const prevPageX = useRef(0);
    const prevPageY = useRef(0);
    const isTouchDevice = useRef(false);
    isTouchDevice.current = checkIfMobileDevice();
    const getValues = () => {
        const { scrollLeft, scrollTop, scrollWidth, scrollHeight, clientWidth, clientHeight, } = (content === null || content === void 0 ? void 0 : content.current) || {};
        return {
            left: scrollLeft / (scrollWidth - clientWidth) || 0,
            top: scrollTop / (scrollHeight - clientHeight) || 0,
            scrollLeft,
            scrollTop,
            scrollWidth,
            scrollHeight,
            clientWidth,
            clientHeight,
        };
    };
    const _update = (callback) => {
        const values = getValues();
        const { scrollLeft, clientWidth, scrollWidth, scrollTop, clientHeight, scrollHeight, } = values;
        const trackHorizontalWidth = getInnerWidth(trackHorizontal && trackHorizontal.current);
        const thumbHorizontalWidth = getThumbHorizontalWidth(content.current, trackHorizontal.current);
        const thumbHorizontalX = (scrollLeft / (scrollWidth - clientWidth)) *
            (trackHorizontalWidth - thumbHorizontalWidth);
        const thumbVerticalHeight = getThumbVerticalHeight(content.current, trackVertical.current);
        const thumbVerticalY = (scrollTop / (scrollHeight - clientHeight)) *
            (getInnerHeight(trackVertical && trackVertical.current) -
                thumbVerticalHeight);
        if (hideTracksWhenNotNeeded) {
            if (trackHorizontal && trackHorizontal.current) {
                trackHorizontal.current.style.visibility =
                    scrollWidth > clientWidth ? 'visible' : 'hidden';
            }
            if (trackVertical && trackVertical.current) {
                trackVertical.current.style.visibility =
                    scrollHeight > clientWidth ? 'visible' : 'hidden';
            }
        }
        /* istanbul ignore else -- @preserve */
        if (thumbHorizontal && thumbHorizontal.current) {
            thumbHorizontal.current.style.width = `${thumbHorizontalWidth}px`;
            /* istanbul ignore if -- @preserve */
            if (thumbHorizontalWidth === 0) {
                thumbHorizontal.current.style.minWidth = '0';
            }
            thumbHorizontal.current.style.transform = `translateX(${thumbHorizontalX}px)`;
        }
        /* istanbul ignore else -- @preserve */
        if (thumbVertical && thumbVertical.current) {
            thumbVertical.current.style.height = `${thumbVerticalHeight}px`;
            /* istanbul ignore if -- @preserve */
            if (thumbVerticalHeight === 0) {
                thumbVertical.current.style.minHeight = '0';
            }
            thumbVertical.current.style.transform = `translateY(${thumbVerticalY}px)`;
        }
        /* istanbul ignore else -- @preserve */
        if (onUpdate)
            onUpdate(values);
        if (typeof callback !== 'function')
            return;
        callback(values);
    };
    const _raf = (callback) => {
        if (requestFrame.current)
            cancelAnimationFrame(requestFrame.current);
        requestFrame.current = requestAnimationFrame(() => {
            requestFrame.current = undefined;
            callback();
        });
    };
    const update = (callback) => {
        _raf(() => _update(callback));
    };
    const _onScrollStart = () => {
        /* istanbul ignore else -- @preserve */
        if (onScrollStart)
            onScrollStart();
    };
    const _onScrollStop = () => {
        /* istanbul ignore else -- @preserve */
        if (onScrollStop)
            onScrollStop();
    };
    const _detectScrolling = () => {
        /* istanbul ignore if -- @preserve */
        if (scrolling.current)
            return;
        scrolling.current = true;
        _onScrollStart();
        // @ts-ignore
        detectScrollingInterval.current = setInterval(() => {
            /* istanbul ignore else -- @preserve */
            if (lastViewScroll &&
                content.current &&
                lastViewScroll.left === content.current.scrollLeft &&
                lastViewScroll.top === content.current.scrollTop) {
                clearInterval(detectScrollingInterval.current);
                scrolling.current = false;
                _onScrollStop();
            }
            setLastViewScroll({
                left: content.current.scrollLeft,
                top: content.current.scrollTop,
            });
        }, 100);
    };
    const _onScroll = (event) => {
        /* istanbul ignore else -- @preserve */
        if (onScroll)
            onScroll(event);
        update((values) => {
            const { scrollLeft, scrollTop } = values;
            content.current.scrollLeft = scrollLeft;
            content.current.scrollTop = scrollTop;
        });
        _detectScrolling();
    };
    const _onTrackMouseEnter = () => {
        trackMouseOver.current = true;
    };
    const _onTrackMouseLeave = () => {
        trackMouseOver.current = false;
    };
    const _onDrag = (event) => {
        var _a, _b;
        if (prevPageX.current && trackHorizontal && trackHorizontal.current) {
            const { clientX } = event;
            const { left: trackLeft } = 
            // eslint-disable-next-line no-unsafe-optional-chaining
            (_a = trackHorizontal.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const clickPosition = getThumbHorizontalWidth(content.current, trackHorizontal.current) -
                prevPageX.current;
            const offset = -trackLeft + clientX - clickPosition;
            const left = getScrollLeftForOffset(offset, content.current, trackHorizontal.current);
            content.current.scrollLeft = left;
        }
        if (prevPageY.current && trackVertical && trackVertical.current) {
            const { clientY } = event;
            // eslint-disable-next-line no-unsafe-optional-chaining
            const { top: trackTop } = (_b = trackVertical.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
            const clickPosition = getThumbVerticalHeight(content.current, trackVertical.current) -
                prevPageY.current;
            const offset = -trackTop + clientY - clickPosition;
            const top = getScrollTopForOffset(offset, content.current, trackVertical.current);
            content.current.scrollTop = top;
        }
        return false;
    };
    const teardownDragging = () => {
        /* istanbul ignore else -- @preserve */
        if (typeof document !== 'undefined') {
            /* istanbul ignore if -- @preserve */
            if (isTouchDevice.current) {
                document.removeEventListener('touchmove', (e) => _onDrag(e === null || e === void 0 ? void 0 : e.touches[0]));
                // eslint-disable-next-line no-use-before-define
                document.removeEventListener('touchend', () => _onDragEnd());
            }
            else {
                document.removeEventListener('mousemove', _onDrag);
                // eslint-disable-next-line
                document.removeEventListener('mouseup', _onDragEnd);
            }
        }
    };
    const _onDragEnd = () => {
        dragging.current = false;
        prevPageX.current = 0;
        prevPageY.current = 0;
        teardownDragging();
    };
    const setupDragging = () => {
        /* istanbul ignore else -- @preserve */
        if (typeof document !== 'undefined') {
            if (isTouchDevice.current) {
                document.addEventListener('touchmove', (e) => _onDrag(e === null || e === void 0 ? void 0 : e.touches[0]), {
                    passive: false,
                });
                document.addEventListener('touchend', () => _onDragEnd());
            }
            else {
                document.addEventListener('mousemove', _onDrag);
                document.addEventListener('mouseup', _onDragEnd);
            }
        }
    };
    const handleDragStart = (event) => {
        dragging.current = true;
        if (event === null || event === void 0 ? void 0 : event.stopImmediatePropagation) {
            event.stopImmediatePropagation();
        }
        setupDragging();
    };
    const _onHorizontalThumbMouseDown = (event) => {
        if (event === null || event === void 0 ? void 0 : event.preventDefault) {
            event.preventDefault();
        }
        handleDragStart(event);
        const { clientX } = event;
        const target = event.target;
        const { offsetWidth } = target;
        const { left } = target.getBoundingClientRect();
        prevPageX.current = offsetWidth - (clientX - left);
    };
    const _onVerticalThumbMouseDown = (event) => {
        if (event === null || event === void 0 ? void 0 : event.preventDefault) {
            event.preventDefault();
        }
        handleDragStart(event);
        const { clientY } = event;
        const target = event.target;
        const { offsetHeight } = target;
        const { top } = target.getBoundingClientRect();
        prevPageY.current = offsetHeight - (clientY - top);
    };
    const _addListeners = () => {
        /* istanbul ignore if -- @preserve */
        if (typeof document === 'undefined' || !content.current)
            return;
        content.current.addEventListener('scroll', _onScroll);
        if (trackHorizontal === null || trackHorizontal === void 0 ? void 0 : trackHorizontal.current) {
            if (isTouchDevice.current) {
                trackHorizontal.current.addEventListener('touchstart', (e) => _onHorizontalTrackMouseDown(e === null || e === void 0 ? void 0 : e.touches[0], content.current, trackHorizontal.current), { passive: true });
                thumbHorizontal.current.addEventListener('touchstart', (e) => _onHorizontalThumbMouseDown(e === null || e === void 0 ? void 0 : e.touches[0]), { passive: true });
            }
            else {
                trackHorizontal.current.addEventListener('mouseenter', _onTrackMouseEnter);
                trackHorizontal.current.addEventListener('mouseleave', _onTrackMouseLeave);
                trackHorizontal.current.addEventListener('mousedown', (event) => _onHorizontalTrackMouseDown(event, content.current, trackHorizontal.current));
                thumbHorizontal.current.addEventListener('mousedown', _onHorizontalThumbMouseDown);
            }
        }
        if (trackVertical === null || trackVertical === void 0 ? void 0 : trackVertical.current) {
            if (isTouchDevice.current) {
                trackVertical.current.addEventListener('touchstart', (e) => _onVerticalTrackMouseDown(e === null || e === void 0 ? void 0 : e.touches[0], content.current, trackVertical.current), { passive: true });
                thumbVertical.current.addEventListener('touchstart', (e) => _onVerticalThumbMouseDown(e === null || e === void 0 ? void 0 : e.touches[0]), { passive: true });
            }
            else {
                trackVertical.current.addEventListener('mouseenter', _onTrackMouseEnter);
                trackVertical.current.addEventListener('mouseleave', _onTrackMouseLeave);
                trackVertical.current.addEventListener('mousedown', (event) => _onVerticalTrackMouseDown(event, content.current, trackVertical.current));
                thumbVertical.current.addEventListener('mousedown', _onVerticalThumbMouseDown);
            }
        }
        /* istanbul ignore else -- @preserve */
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', () => update());
        }
    };
    const removeListeners = () => {
        /* istanbul ignore if -- @preserve */
        if (typeof document === 'undefined' || !content.current)
            return;
        content.current.removeEventListener('scroll', _onScroll);
        if (trackHorizontal === null || trackHorizontal === void 0 ? void 0 : trackHorizontal.current) {
            if (isTouchDevice.current) {
                trackHorizontal.current.removeEventListener('touchstart', (e) => _onHorizontalTrackMouseDown(e === null || e === void 0 ? void 0 : e.touches[0], content.current, trackHorizontal.current));
                thumbHorizontal.current.removeEventListener('touchstart', (e) => _onHorizontalThumbMouseDown(e === null || e === void 0 ? void 0 : e.touches[0]));
            }
            else {
                trackHorizontal.current.removeEventListener('mouseenter', _onTrackMouseEnter);
                trackHorizontal.current.removeEventListener('mouseleave', _onTrackMouseLeave);
                trackHorizontal.current.removeEventListener('mousedown', (event) => _onHorizontalTrackMouseDown(event, content.current, trackHorizontal.current));
                thumbHorizontal.current.removeEventListener('mousedown', _onHorizontalThumbMouseDown);
            }
        }
        if (trackVertical === null || trackVertical === void 0 ? void 0 : trackVertical.current) {
            if (isTouchDevice.current) {
                trackVertical.current.removeEventListener('touchstart', (e) => _onVerticalTrackMouseDown(e === null || e === void 0 ? void 0 : e.touches[0], content.current, trackVertical.current));
                thumbVertical.current.removeEventListener('touchstart', (e) => _onVerticalThumbMouseDown(e === null || e === void 0 ? void 0 : e.touches[0]));
            }
            else {
                trackVertical.current.removeEventListener('mouseenter', _onTrackMouseEnter);
                trackVertical.current.removeEventListener('mouseleave', _onTrackMouseLeave);
                trackVertical.current.removeEventListener('mousedown', (event) => _onVerticalTrackMouseDown(event, content.current, trackVertical.current));
                thumbVertical.current.removeEventListener('mousedown', _onVerticalThumbMouseDown);
            }
        }
        /* istanbul ignore else -- @preserve */
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', () => update());
        }
        // Possibly setup by `handleDragStart`
        teardownDragging();
    };
    useEffect(() => {
        _addListeners();
        update();
        const childrenContent = scrollView.current;
        /* istanbul ignore if -- @preserve */
        if (childContentResizeObserver.current == null) {
            childContentResizeObserver.current =
                typeof window !== 'undefined' &&
                    new ResizeObserver(() => {
                        window.requestAnimationFrame(() => {
                            const newHeight = childrenContent && childrenContent.scrollHeight;
                            if (newHeight !== prevHeight.current) {
                                prevHeight.current = newHeight;
                                update();
                            }
                        });
                    });
            if (childrenContent && childContentResizeObserver.current) {
                childContentResizeObserver.current.observe(childrenContent);
            }
        }
        return () => {
            removeListeners();
            /* istanbul ignore else -- @preserve */
            if (childrenContent && childContentResizeObserver.current) {
                childContentResizeObserver.current.unobserve(childrenContent);
            }
            cancelAnimationFrame(requestFrame.current);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            clearTimeout(hideTracksTimeout.current);
            clearInterval(detectScrollingInterval.current);
        };
    });
    // extraSpacing is used to determine if the content margin is set to '0px' on either side to prevent interactive elements's focus outline from being cut off by the scrollbar.
    const extraSpacing = (side) => contentMargin && contentMargin[side] && contentMargin[side] === '0px'
        ? '3px'
        : '0px';
    const _calculateScrollContainerSpacing = () => `
    ${scrollContainerInset && scrollContainerInset.top ? scrollContainerInset.top : '0px'}
    ${scrollContainerInset && scrollContainerInset.right ? `calc(${scrollContainerInset.right} - ${extraSpacing('right')})` : '-3px'}
    ${scrollContainerInset && scrollContainerInset.bottom ? scrollContainerInset.bottom : '0px'}
    ${scrollContainerInset && scrollContainerInset.left ? `calc(${scrollContainerInset.left} - ${extraSpacing('right')})` : '-3px'}
  `;
    const _calculateContentSpacing = () => `
    ${contentMargin && contentMargin.top ? contentMargin.top : '0px'}
    ${contentMargin && contentMargin.right ? `calc(${contentMargin.right} + ${extraSpacing('right')})` : '3px'}
    ${contentMargin && contentMargin.bottom ? contentMargin.bottom : '0px'}
    ${contentMargin && contentMargin.left ? `calc(${contentMargin.left} + ${extraSpacing('left')})` : '3px'}
  `;
    // - 4px to account for the width of scollbar / thumb
    const _calculateAlignment = () => {
        if (minimizeHitArea) {
            return 0;
        }
        return calculateRem(hitAreaWidth / 2 - 2);
    };
    const _renderScrollbarTrackWithThumb = (orientation) => {
        const _verticalTrackInset = `
      ${verticalScrollbarInset && verticalScrollbarInset.top ? verticalScrollbarInset.top : '0px'}
      ${verticalScrollbarInset && verticalScrollbarInset.right ? verticalScrollbarInset.right : '0px'}
      ${verticalScrollbarInset && verticalScrollbarInset.bottom ? verticalScrollbarInset.bottom : '0px'}
      auto
    `;
        const _horizontalTrackInset = `
      auto
      ${horizontalScrollbarInset && horizontalScrollbarInset.right ? horizontalScrollbarInset.right : '0px'}
      ${horizontalScrollbarInset && horizontalScrollbarInset.bottom ? horizontalScrollbarInset.bottom : '0px'}
      ${horizontalScrollbarInset && horizontalScrollbarInset.left ? horizontalScrollbarInset.left : '0px'}
    `;
        const _verticalTrackGrabZoneStyle = {
            width: `${minimizeHitArea ? '100%' : calculateRem(hitAreaWidth)}`,
            right: `-${_calculateAlignment()}`,
        };
        const _horizontalTrackGrabZoneStyle = {
            height: `${minimizeHitArea ? '100%' : calculateRem(hitAreaWidth)}`,
            bottom: `-${_calculateAlignment()}`,
        };
        const _verticalThumbGrabzoneStyle = {
            width: `${minimizeHitArea ? calculateRem('4px') : calculateRem(hitAreaWidth)}`,
            right: `-${_calculateAlignment()}`,
        };
        const _horizontalThumbGrabzoneStyle = {
            height: `${minimizeHitArea ? calculateRem('4px') : calculateRem(hitAreaWidth)}`,
            bottom: `-${_calculateAlignment()}`,
        };
        return (jsxs("span", { id: `scrollable-${orientation}-track-${scrollableId}`, "aria-hidden": true, className: orientation === 'vertical'
                ? verticalTrackStyles.join(' ')
                : horizontalTrackStyles.join(' '), style: {
                inset: orientation === 'vertical'
                    ? _verticalTrackInset
                    : _horizontalTrackInset,
            }, "data-testid": `${testId}-${orientation}-track`, children: [jsx("span", { className: orientation === 'vertical'
                        ? verticalTrackGrabzoneStyles.join(' ')
                        : horizontalTrackGrabzoneStyles.join(' '), ref: orientation === 'vertical' ? trackVertical : trackHorizontal, "data-testid": `${testId}-${orientation}-track-grabzone`, style: orientation === 'vertical'
                        ? _verticalTrackGrabZoneStyle
                        : _horizontalTrackGrabZoneStyle }), jsx("span", { ref: orientation === 'vertical' ? thumbVertical : thumbHorizontal, className: orientation === 'vertical'
                        ? verticalThumbStyles.join(' ')
                        : horizontalThumbStyles.join(' '), "data-testid": `${testId}-${orientation}-thumb`, children: jsx("span", { style: orientation === 'vertical'
                            ? _verticalThumbGrabzoneStyle
                            : _horizontalThumbGrabzoneStyle, className: orientation === 'vertical'
                            ? verticalThumbGrabzoneStyles.join(' ')
                            : horizontalThumbGrabzoneStyles.join(' '), "data-testid": `${testId}-${orientation}-grabzone` }) })] }));
    };
    return (jsxs("div", { style: {
            height,
            width,
            padding: _calculateScrollContainerSpacing(),
        }, className: containerStyles.join(' '), id: `scrollable-container-${scrollableId}`, "data-testid": `${testId}-container`, children: [jsx("div", { className: styles.content, style: Object.assign(Object.assign({}, contentStyle), { padding: _calculateContentSpacing(), inset: _calculateScrollContainerSpacing() }), ref: content, id: `scrollable-content-${scrollableId}`, "data-testid": `${testId}-content`, tabIndex: tabIndex, children: jsx("div", { ref: scrollView, children: children }) }), (overflowOrientation === 'vertical' || overflowOrientation === 'both') &&
                _renderScrollbarTrackWithThumb('vertical'), (overflowOrientation === 'horizontal' ||
                overflowOrientation === 'both') &&
                _renderScrollbarTrackWithThumb('horizontal')] }));
};

export { ScrollableContainer as default };
